/**
 * EplLabsGrid - Grid de Tarjetas EPL para Dashboard Principal
 * 
 * Muestra las 9 tarjetas EPL filtradas por sistemas asignados al usuario
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { eplApi } from '../epl_modules/services/epl_api';
import { MessageSquare, Users, Target, BarChart, TrendingUp, Lightbulb, Settings, Shield, Map } from 'lucide-react';

// Mapeo de systemCard a configuración visual
const CARD_CONFIG = {
  // EPL-Leader Lab (Morado)
  'gestion_crisis': {
    title: 'Gestión de Crisis',
    description: 'Entrena tu capacidad de liderazgo en situaciones críticas y de alta presión.',
    icon: MessageSquare,
    bgColor: 'bg-purple-500',
    buttonGradient: 'from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950',
    available: true
  },
  'dinamicas_equipo': {
    title: 'Dinámicas de Equipo',
    description: 'Simula escenarios de liderazgo de equipos y resolución de conflictos.',
    icon: Users,
    bgColor: 'bg-purple-600',
    buttonGradient: 'from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950',
    available: true
  },
  'comunicacion_estrategica': {
    title: 'Comunicación Estratégica',
    description: 'Practica comunicación ejecutiva y presentaciones de alto impacto.',
    icon: Target,
    bgColor: 'bg-purple-700',
    buttonGradient: 'from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950',
    available: true
  },
  
  // EPL-Innova System (Verde)
  'innovafast': {
    title: 'InnovaFast',
    description: 'Identifica problemas y diseña soluciones innovadoras.',
    tagline: 'Piensa como innovador',
    icon: Lightbulb,
    bgColor: 'bg-green-500',
    buttonGradient: 'from-green-700 to-green-900 hover:from-green-800 hover:to-green-950',
    available: true
  },
  'innovatech': {
    title: 'InnovaTech',
    description: 'Valida soluciones tecnológicas reales en el mercado.',
    tagline: 'Construye como emprendedor',
    icon: TrendingUp,
    bgColor: 'bg-green-600',
    buttonGradient: 'from-green-700 to-green-900 hover:from-green-800 hover:to-green-950',
    available: true
  },
  'transforma360': {
    title: 'Transforma360',
    description: 'Implementa innovación a nivel organizacional completo.',
    tagline: 'Escala como líder digital',
    icon: BarChart,
    bgColor: 'bg-green-700',
    buttonGradient: 'from-green-700 to-green-900 hover:from-green-800 hover:to-green-950',
    available: true
  },
  
  // EPL-Decision System (Azul)
  'dss_builder': {
    title: 'DSS Builder',
    description: 'Diseña tableros de control para sistemas de soporte a decisiones con IA.',
    icon: Settings,
    bgColor: 'bg-blue-500',
    buttonGradient: 'from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950',
    available: true
  },
  'analisis_riesgo': {
    title: 'Análisis de Riesgo',
    description: 'Modela escenarios de riesgo y evalúa decisiones estratégicas.',
    icon: Shield,
    bgColor: 'bg-blue-600',
    buttonGradient: 'from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950',
    available: true
  },
  'planificacion_estrategica': {
    title: 'Planificación Estratégica',
    description: 'Simula procesos de planificación estratégica con herramientas IA.',
    icon: Map,
    bgColor: 'bg-blue-700',
    buttonGradient: 'from-blue-700 to-blue-900 hover:from-blue-800 hover:to-blue-950',
    available: true
  }
};

// Mapeo de sistemas EPL a tarjetas
const EPL_SYSTEM_MAPPING = {
  'EPL-Leader Lab': ['gestion_crisis', 'dinamicas_equipo', 'comunicacion_estrategica'],
  'EPL-Innova System': ['innovafast', 'innovatech', 'transforma360'],
  'EPL-Decision System': ['dss_builder', 'analisis_riesgo', 'planificacion_estrategica']
};

// Títulos de secciones
const SECTION_TITLES = {
  'EPL-Leader Lab': {
    title: 'EPL–Leader Lab',
    subtitle: 'Laboratorios especializados en desarrollo y práctica de habilidades de liderazgo'
  },
  'EPL-Innova System': {
    title: 'EPL–Innova System',
    subtitle: 'Metodologías avanzadas para innovación y transformación organizacional'
  },
  'EPL-Decision System': {
    title: 'EPL–Decision System',
    subtitle: 'Sistemas inteligentes para optimización de toma de decisiones'
  }
};

const EplLabsGrid = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔍 EplLabsGrid - useEffect ejecutado. User:', user ? 'Existe' : 'No existe', 'isAuthenticated:', isAuthenticated);
    
    // ✅ FIX: Esperar tanto a user como a isAuthenticated para evitar race condition
    if (user && isAuthenticated) {
      console.log('✅ EplLabsGrid - Usuario autenticado detectado, cargando labs...');
      loadLabs();
    } else {
      console.log('⚠️ EplLabsGrid - Usuario no autenticado o no disponible aún');
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  const loadLabs = async () => {
    try {
      console.log('📡 EplLabsGrid - Iniciando carga de labs desde API...');
      setLoading(true);
      const response = await eplApi.listLabs({ status: 'active', limit: 50 });
      console.log('✅ EplLabsGrid - Labs cargados:', response.data?.length || 0);
      setLabs(response.data || []);
    } catch (error) {
      console.error('❌ EplLabsGrid - Error cargando labs EPL:', error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener systemCards asignados al usuario (ahora son valores individuales)
  const userSystemCards = user?.eplSystemsAssigned || [];
  
  console.log('🔍 [DEBUG] user completo:', user);
  console.log('🔍 [DEBUG] userSystemCards:', userSystemCards);
  console.log('🔍 [DEBUG] userSystemCards.length:', userSystemCards.length);

  // Si el usuario no tiene sistemas asignados, no mostrar nada
  if (userSystemCards.length === 0) {
    console.warn('⚠️ Usuario no tiene systemCards EPL asignados (eplSystemsAssigned)');
    return null;
  }

  // Agrupar systemCards por sistema para mostrar secciones
  const systemGroups = {
    'EPL-Leader Lab': ['gestion_crisis', 'dinamicas_equipo', 'comunicacion_estrategica'],
    'EPL-Innova System': ['innovafast', 'innovatech', 'transforma360'],
    'EPL-Decision System': ['dss_builder', 'analisis_riesgo', 'planificacion_estrategica']
  };

  // Determinar qué secciones mostrar según las tarjetas asignadas
  const groupedSystems = Object.entries(systemGroups)
    .filter(([systemName, systemCards]) => {
      // Mostrar sección si al menos una tarjeta está asignada
      return systemCards.some(card => userSystemCards.includes(card));
    })
    .map(([systemName, systemCards]) => {
      const sectionConfig = SECTION_TITLES[systemName];
      
      return {
        systemName,
        ...sectionConfig,
        cards: systemCards
          .filter(cardKey => userSystemCards.includes(cardKey)) // Solo tarjetas asignadas
          .map(cardKey => ({
            key: cardKey,
            ...CARD_CONFIG[cardKey],
            hasLab: labs.some(lab => lab.systemCard === cardKey && lab.status === 'active')
          }))
      };
    });

  const handleCardClick = async (cardKey, config) => {
    if (!config.available) return;
    
    // ✅ SIEMPRE ir a la vista de selección de labs para esa tarjeta
    // Esto permite al usuario ver todos los labs disponibles y su estado (completado/pendiente)
    // Cache buster: v1.1
    navigate(`/epl/labs/${cardKey}`);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Cargando laboratorios...</p>
      </div>
    );
  }

  return (
    <div className="space-y-12 mt-8">
      {groupedSystems.map((system) => {
        const activeCount = system.cards.filter(c => c.hasLab && c.available).length;
        
        return (
          <div key={system.systemName} className="space-y-4">
            {/* Header de sección */}
            <div className="border-b pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{system.title}</h2>
                  <p className="text-sm text-gray-600 mt-1">{system.subtitle}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  <span className="text-sm text-gray-600">{activeCount} de {system.cards.length} activos</span>
                </div>
              </div>
            </div>

            {/* Grid de tarjetas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {system.cards.map((card) => {
                const Icon = card.icon;
                const isClickable = card.available && card.hasLab;
                
                return (
                  <div
                    key={card.key}
                    onClick={() => isClickable && handleCardClick(card.key, card)}
                    className={`relative rounded-lg shadow-lg overflow-hidden transition-all duration-300 group flex flex-col ${
                      isClickable 
                        ? 'cursor-pointer hover:shadow-xl hover:scale-105' 
                        : 'opacity-90'
                    }`}
                  >
                    {/* Card Header con color */}
                    <div className={`${card.bgColor} p-6 text-white relative overflow-hidden`}>
                      {/* Icono grande en marca de agua */}
                      <div className="absolute -right-4 -bottom-4 opacity-10 pointer-events-none">
                        <Icon className="w-32 h-32" />
                      </div>
                      
                      <div className="flex items-start justify-between mb-3 relative z-10">
                        <Icon className="w-8 h-8" />
                        {card.available ? (
                          <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-medium">
                            Disponible
                          </span>
                        ) : card.isPremium ? (
                          <span className="px-3 py-1 bg-yellow-400 text-yellow-900 rounded-full text-xs font-medium">
                            Premium
                          </span>
                        ) : null}
                      </div>
                      
                      <h3 className="text-xl font-bold mb-2 relative z-10">{card.title}</h3>
                      {card.tagline && (
                        <p className="text-sm italic opacity-90 relative z-10">{card.tagline}</p>
                      )}
                    </div>

                    {/* Card Body */}
                    <div className="bg-white p-6 flex flex-col flex-1">
                      <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-grow">
                        {card.description}
                      </p>
                      
                      {/* Footer de acción minimalista */}
                      <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                        {isClickable ? (
                          <>
                            <span className="text-xs font-medium text-gray-400">Laboratorio activo</span>
                            <div className="flex items-center gap-2 text-sm font-bold text-blue-600 group-hover:text-blue-700 transition-colors">
                              <span className="relative">
                                Entrenar
                                <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-blue-600 transition-all duration-300 group-hover:w-full"></span>
                              </span>
                              <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </div>
                          </>
                        ) : card.available && !card.hasLab ? (
                          <div className="w-full flex items-center justify-center gap-2 py-1 px-3 bg-gray-50 rounded-full border border-gray-100">
                            <span className="w-2 h-2 rounded-full bg-gray-300"></span>
                            <span className="text-xs font-medium text-gray-500">Pendiente de asignación</span>
                          </div>
                        ) : (
                          <div className="w-full flex items-center justify-center gap-2 py-1 px-3 bg-gray-50 rounded-full border border-gray-100 opacity-60">
                            <span className="text-xs font-medium text-gray-400">Próximamente</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EplLabsGrid;
